/**
 * Toasts and modal sheets.
 *
 * The prototype wrote into `#toast-root` and `#modal-root`. Here both live in
 * one provider so any screen can raise a sheet without threading props, and
 * a sheet raised from inside another sheet replaces it — the same behaviour.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useBackToClose } from '../hooks/useBackToClose';

// Toast kinds: 'default' | 'pb'

// Context value shape:
//   toast(text, kind?)  — shows a message for 2.7 seconds. `pb` renders it in tape magenta.
//   openSheet(content)  — opens a modal sheet. Pass null to close.
//   closeSheet()
//   openSos(content)    — full-screen emergency takeover.
//   closeSos()
const UIContext = createContext(null);

const TOAST_MS = 2700;

export function UIProvider({ children }) {
  // Toast state shape: { text, kind, seq } — `seq` forces a re-mount so the
  // rise animation replays on a repeat message.
  const [toastState, setToastState] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [sos, setSos] = useState(null);
  const timer = useRef(null);
  const seq = useRef(0);

  const toast = useCallback((text, kind = 'default') => {
    seq.current += 1;
    setToastState({ text, kind, seq: seq.current });
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setToastState(null), TOAST_MS);
  }, []);

  const openSheet = useCallback((content) => setSheet(content), []);
  const closeSheet = useCallback(() => setSheet(null), []);
  const openSos = useCallback((content) => setSos(content), []);
  const closeSos = useCallback(() => setSos(null), []);

  const value = useMemo(
    () => ({ toast, openSheet, closeSheet, openSos, closeSos }),
    [toast, openSheet, closeSheet, openSos, closeSos],
  );

  return (
    <UIContext.Provider value={value}>
      {children}

      {sheet !== null && <Scrim onDismiss={closeSheet}>{sheet}</Scrim>}

      {sos !== null && (
        <div
          id="sosScreen"
          className="fixed inset-0 z-[120] flex flex-col overflow-y-auto bg-[#B3122B] px-5 py-6 text-white"
        >
          {sos}
        </div>
      )}

      {toastState && (
        <div
          key={toastState.seq}
          className={`pointer-events-none fixed bottom-6 left-1/2 z-[90] max-w-[88vw] -translate-x-1/2 px-5 py-3 text-center font-market text-[12.5px] motion-safe:animate-rise ${
            toastState.kind === 'pb' ? 'bg-gold-500 text-ink-800' : 'bg-ink-800 text-white'
          }`}
          role="status"
          aria-live="polite"
        >
          {toastState.text}
        </div>
      )}
    </UIContext.Provider>
  );
}

/**
 * The dimmed backdrop and the sheet itself.
 * Dismisses on backdrop mousedown and on Escape, like the prototype.
 */
function Scrim({ children, onDismiss }) {
  // Back closes the sheet. It lives above the router, so without this Back
  // changed the page underneath and left the sheet on top.
  useBackToClose(true, onDismiss);
  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-ink-900/70 backdrop-blur-sm sm:items-center sm:p-5"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onDismiss();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onDismiss();
      }}
      role="presentation"
    >
      <div
        className="max-h-[92vh] w-full max-w-[530px] overflow-y-auto bg-white p-5 pb-7 motion-safe:animate-sheet-up"
        role="dialog"
        aria-modal="true"
      >
        <div className="mx-auto mb-4 mt-1.5 h-1 w-9 rounded-full bg-line sm:hidden" />
        {children}
      </div>
    </div>
  );
}

export function useUI() {
  const ctx = useContext(UIContext);
  if (!ctx) throw new Error('useUI must be used inside <UIProvider>');
  return ctx;
}
